import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { ArticleAuthor } from 'site-modules/shared/components/preprod-articles/article-author';

import './review-section.scss';

export function ReviewSection({
  children = null,
  heading = null,
  headingTag: HeadingTag = 'h2',
  subhead = null,
  body = null,
  author = null,
  published = null,
  updated = null,
  collapsedBody = null,
  collapsedChildren = null,
  hideCollapseBtn = false,
  id = null,
  bodyClassName = 'mb-1_5',
  collapsedBodyClasName = null,
  collapseTrackingId,
  className = null,
  headingClassName = 'heading-4 mb-0_5',
  subheadClassName = 'mb-1_5 size-16 text-gray-darker',
  bodyCreativeId = null,
  showMrectAd = false,
  afterBodyChildren = null,
}) {
  const hasBody = get(body, 'length');
  const hasCollapsedBody = get(collapsedBody, 'length');
  if (!hasBody && !hasCollapsedBody && !children) {
    return null;
  }

  const hasCollapse = Boolean(hasCollapsedBody || collapsedChildren);

  return (
    <div id={id} className={className}>
      {heading && <HeadingTag className={headingClassName}>{heading}</HeadingTag>}
      {subhead && <p className={classnames(subheadClassName)}>{subhead}</p>}
      {author && (
        <ArticleAuthor
          author={author}
          lazyLoadImage
          showBioTooltip
          isDateTextBelow
          className="medium align-items-center mb-1"
          byline="Written by automotive expert"
          publishDate={published}
          dateModified={updated}
          tooltipProps={{
            trackingParent: 'edm-entry-editorial-review',
            trackingSubaction: 'view_tooltip_review_author',
          }}
          showPhoto
          updatedLabel="Updated: "
        />
      )}
      {showMrectAd && <MrectNative wrapperClass="mrect float-end ms-1_5 mb-1_5" wiredOnly refreshable={false} />}
      {children}
      {!!hasBody && (
        <ContentFragment
          classes={classnames('review-section-fragment size-16', bodyClassName, {
            first: hasCollapse,
          })}
          componentProps={{ 'data-tracking-parent': bodyCreativeId }}
        >
          {body}
        </ContentFragment>
      )}
      {!hasCollapse && afterBodyChildren}
      {hasCollapse && (
        <ContentCollapse
          isEclipseFade={false}
          btnClasses="px-0_5 py-0 mt-0_75 size-16 text-primary-darker"
          btnContainerClasses="py-0"
          hideCollapseBtn={hideCollapseBtn}
          trackingId={collapseTrackingId}
          justify="justify-content-start"
          showBtnBelow
          btnTextClasses="fw-normal"
          classes="size-16"
          ariaLabelHeading="about this model"
          resetParams={{
            offset: SCROLL_OFFSET,
            duration: SCROLL_DURATION,
            to: id,
          }}
        >
          <ContentFragment
            classes={classnames('review-section-fragment size-16', collapsedBodyClasName, {
              rest: hasBody,
              'mb-1_5': collapsedChildren,
            })}
          >
            {collapsedBody}
          </ContentFragment>
          {collapsedChildren}
          {afterBodyChildren}
        </ContentCollapse>
      )}
    </div>
  );
}

ReviewSection.propTypes = {
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  headingTag: PropTypes.string,
  subhead: PropTypes.string,
  body: PropTypes.string,
  collapsedBody: PropTypes.string,
  collapsedChildren: PropTypes.node,
  hideCollapseBtn: PropTypes.bool,
  id: PropTypes.string,
  bodyClassName: PropTypes.string,
  collapsedBodyClasName: PropTypes.string,
  collapseTrackingId: PropTypes.string,
  className: PropTypes.string,
  headingClassName: PropTypes.string,
  subheadClassName: PropTypes.string,
  bodyCreativeId: PropTypes.string,
  author: EditorialReviewEntities.AuthorData,
  published: PropTypes.string,
  updated: PropTypes.string,
  children: PropTypes.node,
  showMrectAd: PropTypes.bool,
  afterBodyChildren: PropTypes.node,
};
